
//api.ts

import axios from 'axios';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://support.rentremote.com/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh and user-friendly error messages
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      
      // Try refresh if we have both tokens, otherwise just redirect for login
      if (token && refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          
          const { access_token, refresh_token } = response.data;
          localStorage.setItem('token', access_token);
          localStorage.setItem('refreshToken', refresh_token);
          
          return api(originalRequest);
        } catch (refreshError) {
          console.log('Token refresh failed, redirecting to login');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      } else if (token) {
        // We have access token but no refresh token - allow it to fail gracefully
        // Don't auto-redirect on 401 as it might be a valid API error
        console.log('Access token expired and no refresh token available');
      } else {
        // No tokens available - only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          console.log('No authentication tokens found, redirecting to login');
          window.location.href = '/login';
        }
      }
    }
    
    // 14/11/2025 - User Access Management Module
    // Handle 403 Forbidden - Convert to user-friendly message
    if (error.response?.status === 403) {
      // Replace technical error with user-friendly message
      error.message = 'Permission denied. You do not have access to perform this action.';
      
      // Also update the response data for components that check error.response.data
      if (error.response.data) {
        error.response.data.message = 'Permission denied. You do not have access to perform this action.';
        error.response.data.detail = 'Permission denied. You do not have access to perform this action.';
      }
    }
    
    // 14/11/2025 - Handle other common HTTP errors with user-friendly messages
    if (error.response?.status === 400) {
      error.message = error.response.data?.detail || error.response.data?.message || 'Invalid request. Please check your input and try again.';
    }
    
    if (error.response?.status === 404) {
      error.message = error.response.data?.detail || error.response.data?.message || 'The requested resource was not found.';
    }
    
    if (error.response?.status === 500) {
      error.message = 'An unexpected error occurred. Please try again later or contact support.';
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials).then(res => res.data),
  
  register: (userData: any) =>
    api.post('/auth/register', userData).then(res => res.data),
  
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refresh_token: refreshToken }).then(res => res.data),
  
  logout: () =>
    api.post('/auth/logout').then(res => res.data),
};

// Conversations API
export const conversationsAPI = {
  getConversations: (params?: any) =>
    api.get('/conversations', { params }).then(res => res.data),
  
  getConversation: (id: number) =>
    api.get(`/conversations/${id}`).then(res => res.data),
  
  createConversation: (data: any) =>
    api.post('/conversations', data).then(res => res.data),
  
  updateConversation: (id: number, data: any) =>
    api.put(`/conversations/${id}`, data).then(res => res.data),
  
  assignConversation: (id: number, assigneeId?: number, teamId?: number) =>
    api.post(`/conversations/${id}/assign`, { assignee_id: assigneeId, team_id: teamId }).then(res => res.data),
  
  toggleStatus: (id: number, status: string) =>
    api.post(`/conversations/${id}/toggle_status`, { status }).then(res => res.data),
  
  updateLabels: (id: number, labels: number[]) =>
    api.post(`/conversations/${id}/labels`, { labels }).then(res => res.data),
  
  getAvailableAgents: (id: number) =>
    api.get(`/conversations/${id}/available_agents`).then(res => res.data),
  
  addParticipant: (id: number, participantId: number) =>
    api.post(`/conversations/${id}/participants`, { participant_id: participantId }).then(res => res.data),
  
  removeParticipant: (id: number, participantId: number) =>
    api.delete(`/conversations/${id}/participants/${participantId}`).then(res => res.data),
  
  getParticipants: (id: number) =>
    api.get(`/conversations/${id}/participants`).then(res => res.data),
  
  getMetrics: (id: number) =>
    api.get(`/conversations/${id}/metrics`).then(res => res.data),

  /** Canonical AI ↔ Human toggle (PUT — same logic as dashboard controls). */
  setAIEnabled: (id: number, ai_enabled: boolean) =>
    api.put(`/conversations/${id}/ai-toggle`, { ai_enabled }).then(res => res.data),

  /** Flip AI state; uses canonical PUT when ai_enabled is explicit, else PATCH toggle. */
  toggleAI: (id: number, ai_enabled?: boolean) =>
    ai_enabled !== undefined
      ? api.put(`/conversations/${id}/ai-toggle`, { ai_enabled }).then(res => res.data)
      : api.patch(`/conversations/${id}/ai-toggle`).then(res => res.data),
};

// Messages API
export const messagesAPI = {
  getMessages: (conversationId: number, params?: any) =>
    api.get(`/conversations/${conversationId}/messages`, { params }).then(res => res.data),
  
  sendMessage: (conversationId: number, data: any) =>
    api.post(`/conversations/${conversationId}/messages`, data).then(res => res.data),
  
  updateMessage: (id: number, data: any) =>
    api.put(`/messages/${id}`, data).then(res => res.data),
  
  deleteMessage: (id: number) =>
    api.delete(`/messages/${id}`).then(res => res.data),
  
  uploadAttachment: (conversationId: number, file: File, isPrivate: boolean = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('private', isPrivate.toString());
    
    return api.post(`/conversations/${conversationId}/messages/attachment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },
};

// Teams API
export const teamsAPI = {
  getTeams: () =>
    api.get('/teams').then(res => res.data),
  
  createTeam: (data: any) =>
    api.post('/teams', data).then(res => res.data),
  
  updateTeam: (id: number, data: any) =>
    api.put(`/teams/${id}`, data).then(res => res.data),
  
  deleteTeam: (id: number) =>
    api.delete(`/teams/${id}`).then(res => res.data),
  
  getTeamMembers: (id: number) =>
    api.get(`/teams/${id}/members`).then(res => res.data),
  
  addTeamMember: (teamId: number, userId: number, isAdmin = false) =>
    api.post(`/teams/${teamId}/members`, { user_id: userId, is_admin: isAdmin }).then(res => res.data),
  
  removeTeamMember: (teamId: number, userId: number) =>
    api.delete(`/teams/${teamId}/members/${userId}`).then(res => res.data),
};

// Labels API
export const labelsAPI = {
  getLabels: () =>
    api.get('/labels').then(res => res.data),
  
  createLabel: (data: any) =>
    api.post('/labels', data).then(res => res.data),
  
  updateLabel: (id: number, data: any) =>
    api.put(`/labels/${id}`, data).then(res => res.data),
  
  deleteLabel: (id: number) =>
    api.delete(`/labels/${id}`).then(res => res.data),
  
  addLabelToConversation: (conversationId: number, labelId: number) =>
    api.post(`/labels/conversations/${conversationId}/labels/${labelId}`).then(res => res.data),
  
  removeLabelFromConversation: (conversationId: number, labelId: number) =>
    api.delete(`/labels/conversations/${conversationId}/labels/${labelId}`).then(res => res.data),
};

// Canned Responses API
export const cannedResponsesAPI = {
  getCannedResponses: (params?: any) =>
    api.get('/canned-responses', { params }).then(res => res.data),
  
  createCannedResponse: (data: any) =>
    api.post('/canned-responses', data).then(res => res.data),
  
  updateCannedResponse: (id: number, data: any) =>
    api.put(`/canned-responses/${id}`, data).then(res => res.data),
  
  deleteCannedResponse: (id: number) =>
    api.delete(`/canned-responses/${id}`).then(res => res.data),
  
  useCannedResponse: (id: number) =>
    api.post(`/canned-responses/${id}/use`).then(res => res.data),
  
  searchCannedResponses: (query: string) =>
    api.get('/canned-responses/search', { params: { query } }).then(res => res.data),
};

// Reports API
export const reportsAPI = {
  getConversationReport: (params: any) =>
    api.get('/reports/conversations', { params }).then(res => res.data),

  getAgentReport: (params: any) =>
    api.get('/reports/agents', { params }).then(res => res.data),

  getCSATReport: (params: any) =>
    api.get('/reports/csat', { params }).then(res => res.data),

  getDashboardMetrics: () =>
    api.get('/reports/dashboard').then(res => res.data),

  // v4.0 - Dashboard enhancement methods
  getConversationTrends: (params: any) =>
    api.get('/reports/conversations', { params }).then(res => res.data.daily_breakdown || []),

  getChannelDistribution: (params: any) =>
    api.get('/reports/conversations', { params }).then(res => {
      const channelsData = res.data.conversations_by_channel || {};
      const totalConversations = res.data.total_conversations || 0;

      // Transform object into array format expected by Dashboard
      return Object.entries(channelsData).map(([channel_name, count]) => ({
        channel_name,
        channel_type: channel_name as any, // Simplified - you can map to actual types
        total_conversations: count as number,
        percentage: totalConversations > 0 ? ((count as number) / totalConversations) * 100 : 0
      }));
    }),

  getTopAgents: (params: any, limit: number = 5) =>
    api.get('/reports/agents', { params }).then(res => {
      const agents = res.data.agents || [];
      return agents
        .sort((a: any, b: any) => b.conversations_handled - a.conversations_handled)
        .slice(0, limit);
    }),

  getCSATOverview: (params: any) =>
    api.get('/reports/csat', { params }).then(res => {
      const csatData = res.data || {};
      const ratingDistribution = csatData.rating_distribution || {};

      // Transform rating_distribution object to array format expected by Dashboard
      const ratingDistributionArray = Array.isArray(ratingDistribution)
        ? ratingDistribution
        : Object.entries(ratingDistribution).map(([rating, count]) => ({
            rating: parseInt(rating),
            count: count as number,
            percentage: csatData.total_responses > 0
              ? ((count as number) / csatData.total_responses) * 100
              : 0
          })).sort((a, b) => a.rating - b.rating);

      return {
        ...csatData,
        rating_distribution: ratingDistributionArray
      };
    }),
};

// Advanced Reports API
export const advancedReportsAPI = {
  getSentimentAnalysis: (params: any) =>
    api.get('/advanced-reports/sentiment-analysis', { params }).then(res => res.data),

  getConversationAudits: (params: any) =>
    api.get('/advanced-reports/conversation-audits', { params }).then(res => res.data),

  getConversationalAnalytics: (params: any) =>
    api.get('/advanced-reports/conversational-analytics', { params }).then(res => res.data),

  getAggregatedMetrics: (params: any) =>
    api.get('/advanced-reports/aggregated-metrics', { params }).then(res => res.data),

  getTeamAnalytics: (params: any) =>
    api.get('/advanced-reports/team-analytics', { params }).then(res => res.data),

  getSLACompliance: (params: any) =>
    api.get('/advanced-reports/sla-compliance', { params }).then(res => res.data),

  getConversationQuality: (params: any) =>
    api.get('/advanced-reports/conversation-quality', { params }).then(res => res.data),
	
  getLabelAnalytics: (params: any) =>
    api.get('/advanced-reports/label-analytics', { params }).then(res => res.data),	
};

// MCP (Model Context Protocol) Servers API
export const mcpServersAPI = {
  // Get all MCP server configurations
  getServers: () =>
    api.get('/mcp-servers').then(res => res.data),

  // Get specific server configuration
  getServer: (serverId: number) =>
    api.get(`/mcp-servers/${serverId}`).then(res => res.data),

  // Create new MCP server configuration
  createServer: (data: any) =>
    api.post('/mcp-servers', data).then(res => res.data),

  // Update MCP server configuration
  updateServer: (serverId: number, data: any) =>
    api.put(`/mcp-servers/${serverId}`, data).then(res => res.data),

  // Delete MCP server configuration
  deleteServer: (serverId: number) =>
    api.delete(`/mcp-servers/${serverId}`).then(res => res.data),

  // Test connection to MCP server
  testConnection: (serverId: number) =>
    api.post(`/mcp-servers/${serverId}/test`).then(res => res.data),

  // Get tools from specific server
  getServerTools: (serverId: number) =>
    api.get(`/mcp-servers/${serverId}/tools`).then(res => res.data),

  // Refresh tools from all connected MCP servers (fetches fresh tool definitions)
  refreshTools: () =>
    api.post('/mcp-servers/refresh-tools').then(res => res.data),
};

// Contacts API
export const contactsAPI = {
  getContacts: (params?: any) =>
    api.get('/contacts', { params }).then(res => res.data),
  
  getContact: (id: number) =>
    api.get(`/contacts/${id}`).then(res => res.data),
  
  createContact: (data: any) =>
    api.post('/contacts', data).then(res => res.data),
  
  updateContact: (id: number, data: any) =>
    api.put(`/contacts/${id}`, data).then(res => res.data),
  
  deleteContact: (id: number) =>
    api.delete(`/contacts/${id}`).then(res => res.data),
  
  getContactStats: () =>
    api.get('/contacts/stats').then(res => res.data),
  
  exportContacts: () => {
    return api.get('/contacts/export', {
      responseType: 'blob'
    }).then(response => {
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contacts.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  },
  
  importContacts: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post('/contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },
};

// Notes API
export const notesAPI = {
  createNote: (data: any) =>
    api.post('/notes', data).then(res => res.data),
  
  getConversationNotes: (conversationId: number) =>
    api.get(`/notes/conversation/${conversationId}`).then(res => res.data),
  
  deleteNote: (id: number) =>
    api.delete(`/notes/${id}`).then(res => res.data),
  
  getMentions: (unreadOnly = false) =>
    api.get('/notes/mentions', { params: { unread_only: unreadOnly } }).then(res => res.data),
  
  markMentionRead: (id: number) =>
    api.put(`/notes/mentions/${id}/read`).then(res => res.data),
};

// Business Hours API
export const businessHoursAPI = {
  getBusinessHours: () =>
    api.get('/business-hours/').then(res => res.data),
  
  createBusinessHour: (data: any) =>
    api.post('/business-hours/', data).then(res => res.data),
  
  updateBusinessHour: (id: number, data: any) =>
    api.put(`/business-hours/${id}`, data).then(res => res.data),
  
  deleteBusinessHour: (id: number) =>
    api.delete(`/business-hours/${id}`).then(res => res.data),
  
  bulkCreateOrUpdateBusinessHours: (data: any[]) =>
    api.post('/business-hours/bulk', data).then(res => res.data),
  
  getAutoResponders: () =>
    api.get('/business-hours/auto-responders').then(res => res.data),
  
  createAutoResponder: (data: any) =>
    api.post('/business-hours/auto-responders', data).then(res => res.data),
  
  updateAutoResponder: (id: number, data: any) =>
    api.put(`/business-hours/auto-responders/${id}`, data).then(res => res.data),
  
  deleteAutoResponder: (id: number) =>
    api.delete(`/business-hours/auto-responders/${id}`).then(res => res.data),
};

// Agents API
export const agentsAPI = {
  getAgents: () =>
    api.get('/agents/').then(res => res.data),
  
  getAgent: (id: number) =>
    api.get(`/agents/${id}`).then(res => res.data),
  
  createAgent: (data: any) =>
    api.post('/agents/', data).then(res => res.data),
  
  updateAgent: (id: number, data: any) =>
    api.put(`/agents/${id}`, data).then(res => res.data),
  
  deleteAgent: (id: number) =>
    api.delete(`/agents/${id}`).then(res => res.data),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: (params?: any) =>
    api.get('/notifications/', { params }).then(res => res.data),
  
  markAsRead: (id: number) =>
    api.patch(`/notifications/${id}/read`).then(res => res.data),
  
  markAllAsRead: () =>
    api.patch('/notifications/read-all').then(res => res.data),
  
  deleteNotification: (id: number) =>
    api.delete(`/notifications/${id}`).then(res => res.data),
  
  getUnreadCount: () =>
    api.get('/notifications/unread-count').then(res => res.data),
  
  createSample: () =>
    api.post('/notifications/create-sample').then(res => res.data),
};

// Slack Integration API
export const slackAPI = {
  getIntegration: () =>
    api.get('/slack/').then(res => res.data),
  
  createIntegration: (data: any) =>
    api.post('/slack/', data).then(res => res.data),
  
  updateIntegration: (data: any) =>
    api.put('/slack/', data).then(res => res.data),
  
  deleteIntegration: () =>
    api.delete('/slack/').then(res => res.data),
  
  getChannels: () =>
    api.get('/slack/channels').then(res => res.data).catch(error => {
      if (error.response?.status === 404) {
        return { channels: [] };
      }
      throw error;
    }),
  
  getOAuthUrl: () =>
    api.get('/slack/oauth/url').then(res => res.data),
  
  testIntegration: () =>
    api.post('/slack/test').then(res => res.data),
};

// Inboxes API
export const inboxesAPI = {
  getInboxes: () =>
    api.get('/inboxes').then(res => res.data),
  
  getInbox: (id: number) =>
    api.get(`/inboxes/${id}`).then(res => res.data),
  
  createInbox: (data: any) =>
    api.post('/inboxes', data).then(res => res.data),
  
  updateInbox: (id: number, data: any) =>
    api.put(`/inboxes/${id}`, data).then(res => res.data),
  
  deleteInbox: (id: number) =>
    api.delete(`/inboxes/${id}`).then(res => res.data),
  
  getInboxStats: (id: number) =>
    api.get(`/inboxes/${id}/stats`).then(res => res.data),
  
  fixAPIChannels: () =>
    api.post('/inboxes/fix-api-channels').then(res => res.data),
  
  setAgentBot: (id: number, agentBotId?: number) =>
    api.post(`/inboxes/${id}/set_agent_bot`, { agent_bot_id: agentBotId }).then(res => res.data),

  // Channel-level AI Settings
  getAISettings: (id: number) =>
    api.get(`/inboxes/${id}/ai-settings`).then(res => res.data),

  updateAISettings: (id: number, aiEnabled: boolean) =>
    api.put(`/inboxes/${id}/ai-settings`, { ai_enabled: aiEnabled }).then(res => res.data),
    
  toggleInboxAI: (inboxId: number, aiEnabled: boolean) =>
    api.patch(`/inboxes/${inboxId}/toggle-ai`, { ai_enabled: aiEnabled }).then(res => res.data),    
};

// API Channels API
export const apiChannelsAPI = {
  getProviders: () =>
    api.get('/api_channels/providers').then(res => res.data),
  
  getProviderConfig: (provider: string) =>
    api.get(`/api_channels/providers/${provider}/config`).then(res => res.data),
  
  getChannels: () =>
    api.get('/api_channels/channels').then(res => res.data),
  
  getChannel: (id: number) =>
    api.get(`/api_channels/channels/${id}`).then(res => res.data),
  
  createChannel: (data: any) =>
    api.post('/api_channels/channels', data).then(res => res.data),
  
  updateChannel: (id: number, data: any) =>
    api.put(`/api_channels/channels/${id}`, data).then(res => res.data),
  
  deleteChannel: (id: number) =>
    api.delete(`/api_channels/channels/${id}`).then(res => res.data),
  
  verifyChannel: (id: number) =>
    api.post(`/api_channels/channels/${id}/verify`).then(res => res.data),
  
  testConnection: (data: any) =>
    api.post('/api_channels/test-connection', data).then(res => res.data),
};

// Captain AI API with fallback for development
const createAIAPICall = (endpoint: string, method: string = 'GET', data?: any) => {
  const apiCall = method === 'GET'
    ? api.get(endpoint)
    : method === 'POST'
      ? api.post(endpoint, data)
      : method === 'PUT'
        ? api.put(endpoint, data)
        : api.delete(endpoint);

  return apiCall
    .then(res => res.data)
    .catch(err => {
      console.warn(`AI API call failed (${endpoint}):`, err.message);
      // Return null for failed API calls - component will handle gracefully
      return null;
    });
};

export const captainAIAPI = {
  getConfig: () =>
    createAIAPICall('/captain-ai/config'),

  updateConfig: (data: any) =>
    api.put('/captain-ai/config', data).then(res => res.data),

  generateAutoResponse: (conversationId: number, data: any) =>
    createAIAPICall('/captain-ai/auto_response', 'POST', {
      conversation_id: conversationId,
      ...data
    }),

  getChatSuggestion: (conversationId: number, messageContent: string) =>
    createAIAPICall('/captain-ai/chat-suggestion', 'POST', {
      conversation_id: conversationId,
      message_content: messageContent
    }),

  getAgentSuggestion: (conversationId: number) =>
    createAIAPICall(`/captain-ai/agent-suggestion/${conversationId}`),

  markSuggestionUsed: (suggestionId: number) =>
    createAIAPICall(`/captain-ai/suggestions/${suggestionId}/used`, 'POST'),

  markSuggestionDismissed: (suggestionId: number) =>
    createAIAPICall(`/captain-ai/suggestions/${suggestionId}/dismissed`, 'POST'),

  getFAQPatterns: () =>
    createAIAPICall('/captain-ai/faq-patterns'),

  createFAQPattern: (data: any) =>
    createAIAPICall('/captain-ai/faq-patterns', 'POST', data),

  updateFAQPattern: (id: number, data: any) =>
    createAIAPICall(`/captain-ai/faq-patterns/${id}`, 'PUT', data),

  deleteFAQPattern: (id: number) =>
    createAIAPICall(`/captain-ai/faq-patterns/${id}`, 'DELETE'),

  detectFAQ: (messageContent: string) =>
    createAIAPICall('/captain-ai/detect-faq', 'POST', { message_content: messageContent }),

  getConversationMemory: (conversationId: number) =>
    createAIAPICall(`/captain-ai/conversation-memory/${conversationId}`),

  getAnalytics: (params?: any) =>
    createAIAPICall('/captain-ai/analytics'),

  getKnowledgeBase: () =>
    createAIAPICall('/captain-ai/knowledge-base'),

  createKnowledgeBase: (data: any) =>
    createAIAPICall('/captain-ai/knowledge-base', 'POST', data),

  updateKnowledgeBase: (id: number, data: any) =>
    createAIAPICall(`/captain-ai/knowledge-base/${id}`, 'PUT', data),

  deleteKnowledgeBase: (id: number) =>
    createAIAPICall(`/captain-ai/knowledge-base/${id}`, 'DELETE'),
};

// Automation Rules API
export const automationAPI = {
  getAutomationRules: (params?: any) =>
    api.get('/automation-rules/', { params }).then(res => res.data),

  getAutomationRule: (id: number) =>
    api.get(`/automation-rules/${id}`).then(res => res.data),

  createAutomationRule: (data: any) =>
    api.post('/automation-rules/', data).then(res => res.data),

  updateAutomationRule: (id: number, data: any) =>
    api.put(`/automation-rules/${id}`, data).then(res => res.data),

  deleteAutomationRule: (id: number) =>
    api.delete(`/automation-rules/${id}`).then(res => res.data),

  toggleAutomationRule: (id: number, active: boolean) =>
    api.patch(`/automation-rules/${id}/toggle`, { active }).then(res => res.data),

  cloneAutomationRule: (id: number) =>
    api.post(`/automation-rules/${id}/clone`).then(res => res.data),

  getAutomationExecutions: (ruleId: number, limit: number = 50) =>
    api.get(`/automation-rules/${ruleId}/executions`, { params: { limit } }).then(res => res.data),

  getAutomationAnalytics: (ruleId: number) =>
    api.get(`/automation-rules/${ruleId}/analytics`).then(res => res.data),
};

// Email Channels API
export const emailChannelsAPI = {
  // List all email channels
  getChannels: () =>
    api.get('/email/channels').then(res => res.data),

  // Get single channel details
  getChannel: (id: number) =>
    api.get(`/email/channels/${id}`).then(res => res.data),

  // Create new channel
  createChannel: (data: any) =>
    api.post('/email/channels', data).then(res => res.data),

  // Update channel
  updateChannel: (id: number, data: any) =>
    api.put(`/email/channels/${id}`, data).then(res => res.data),

  // Verify channel connection
  verifyChannel: (id: number) =>
    api.post(`/email/channels/${id}/verify`).then(res => res.data),

  // Get template configuration
  getTemplateConfig: (id: number) =>
    api.get(`/email/channels/${id}/template`).then(res => res.data),

  // Update template configuration
  updateTemplateConfig: (id: number, data: any) =>
    api.put(`/email/channels/${id}/template`, data).then(res => res.data),

  // Preview template
  previewTemplate: (id: number, data: any) =>
    api.post(`/email/channels/${id}/template/preview`, data).then(res => res.data),

  // Send test email
  sendTestEmail: (id: number, data: any) =>
    api.post(`/email/channels/${id}/template/test`, data).then(res => res.data),

  // Get providers
  getProviders: () =>
    api.get('/email/providers').then(res => res.data),

  // Get provider config
  getProviderConfig: (provider: string) =>
    api.get(`/email/providers/${provider}/config`).then(res => res.data),

  // Upload OAuth files (credentials.json and/or token.json)
  uploadOAuthFiles: (credentials?: File, token?: File) => {
    const formData = new FormData();
    if (credentials) {
      formData.append('credentials', credentials);
    }
    if (token) {
      formData.append('token', token);
    }

    return api.post('/email/upload-credentials', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },

  // Parse credentials.json file
  parseCredentialsFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/email/parse-credentials', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },

  // Parse token.json file
  parseTokenFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/email/parse-token', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },
};

// Custom Roles API
// Added: 2024-11-12 - User Access Management Module Phase 4.1
export const customRolesAPI = {
  // List all custom roles
  getRoles: () =>
    api.get('/custom-roles').then(res => res.data),

  // Get single role with permissions
  getRole: (id: number) =>
    api.get(`/custom-roles/${id}`).then(res => res.data),

  // Create new custom role
  createRole: (data: any) =>
    api.post('/custom-roles', data).then(res => res.data),

  // Update custom role
  updateRole: (id: number, data: any) =>
    api.put(`/custom-roles/${id}`, data).then(res => res.data),

  // Delete custom role
  deleteRole: (id: number) =>
    api.delete(`/custom-roles/${id}`).then(res => res.data),

  // Get role permissions
  getRolePermissions: (id: number) =>
    api.get(`/custom-roles/${id}/permissions`).then(res => res.data),

  // Update role permissions (bulk)
  updateRolePermissions: (id: number, permissions: string[]) =>
    api.put(`/custom-roles/${id}/permissions`, { permissions }).then(res => res.data),

  // Clone role
  cloneRole: (id: number, name: string) =>
    api.post(`/custom-roles/${id}/clone`, { name }).then(res => res.data),
};

// Permissions API
// Added: 2024-11-12 - User Access Management Module Phase 4.1
export const permissionsAPI = {
  // List all permissions
  getAllPermissions: (params?: any) =>
    api.get('/permissions', { params }).then(res => res.data),

  // List permissions grouped by category
  getPermissionsGrouped: () =>
    api.get('/permissions/grouped').then(res => res.data),

  // List all categories
  getCategories: () =>
    api.get('/permissions/categories').then(res => res.data),

  // Get current user's permissions
  getMyPermissions: () =>
    api.get('/permissions/me').then(res => res.data),

  // Get specific user's permissions
  getUserPermissions: (userId: number) =>
    api.get(`/permissions/user/${userId}`).then(res => res.data),

  // Check single permission
  checkPermission: (permission: string) =>
    api.get('/permissions/check', { params: { permission } }).then(res => res.data),

  // Check multiple permissions
  checkMultiplePermissions: (permissions: string[], checkType: 'any' | 'all' = 'any') =>
    api.post('/permissions/check-multiple', permissions, { params: { check_type: checkType } }).then(res => res.data),
};

export default api;